import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { OptimizedImage } from "@/components/OptimizedImage";
import {
  ArrowRight,
  Award,
  BookOpen,
  Calculator,
  CheckCircle2,
  GitCompare,
  Target,
  TrendingUp,
  Users,
} from "lucide-react";
import heroImage from "@/assets/hero-trading-chart.webp";
import { useNavigate } from "react-router-dom";
import NewsletterSignup from "@/components/NewsletterSignup";
import { NavigationHelper } from "@/lib/navigation";
import { SEOHead } from "@/components/seo/SEOHead";
import { propFirms } from "@/data/propFirms";

const BASE_URL = "https://www.futures-trader-hub.com";

const reviewMap: Record<string, string> = {
  apex: "/apex-trader-funding-review",
};

const trustIndicators = [
  { icon: CheckCircle2, label: "Independent Reviews" },
  { icon: BookOpen, label: "Trading Education" },
  { icon: GitCompare, label: "Prop Firm Comparisons" },
  { icon: Users, label: "Real Trader Resources" },
];

const featuredFirmIds = [
  "apex",
  "topstep",
  "myfundedfutures",
  "tradeday",
  "take-profit-trader",
  "elite-trader-funding",
];

const calculators = [
  {
    icon: Target,
    title: "Apex Drawdown Calculator",
    description: "Track your Apex trailing drawdown in real time across every account size.",
    href: "/apex-trailing-drawdown-calculator",
  },
  {
    icon: Award,
    title: "Apex Consistency Calculator",
    description: "Verify the 50% consistency rule before requesting your next payout.",
    href: "/apex-consistency-rule-explained",
  },
  {
    icon: Calculator,
    title: "Futures Tick Value Calculator",
    description: "Calculate tick value and P&L for ES, NQ, MES, MNQ, CL, GC, and more.",
    href: "/calculators/tick-value",
  },
  {
    icon: TrendingUp,
    title: "Position Size Calculator",
    description: "Size every futures trade to your account risk and stop distance.",
    href: "/calculators/position-size",
  },
  {
    icon: Target,
    title: "Risk / Reward Calculator",
    description: "Plan entries, stops, and targets with a proper risk-to-reward ratio.",
    href: "/calculators/risk-reward",
  },
];

const educationCards = [
  { title: "Futures Trading Guide", description: "Complete beginner's guide to futures trading.", href: "/futures-trading-guide", cta: "Read Guide" },
  { title: "Day vs Swing Trading", description: "Compare trading styles and choose your approach.", href: "/day-trading-vs-swing-trading", cta: "Compare Styles" },
  { title: "Margin Requirements", description: "Understanding leverage and margin in futures.", href: "/margin-requirements", cta: "Learn Margin" },
];

const HomePage = () => {
  const navigate = useNavigate();
  NavigationHelper.setNavigate(navigate);

  const featuredFirms = featuredFirmIds
    .map((id) => propFirms.find((f) => f.id === id))
    .filter((f): f is NonNullable<typeof f> => Boolean(f));

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Best Futures Prop Firms 2026",
    itemListElement: featuredFirms.map((f, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: f.name,
      url: `${BASE_URL}${reviewMap[f.id] || `/prop-firms`}`,
    })),
  };

  return (
    <>
      <SEOHead
        title="Best Futures Prop Firms 2026 — Reviews, Discounts & Comparisons"
        description="Compare the best futures prop firms, find verified discount codes, calculate drawdowns, and learn how to pass evaluations faster. Independent reviews and trader resources."
        canonicalUrl="/"
        keywords="futures prop firms, best prop firms, prop firm reviews, prop firm comparison, apex trader funding, topstep, funded trader, futures trading"
        structuredData={itemListSchema}
      />
      <div className="min-h-screen bg-background">
        <div className="hidden" aria-hidden="true">Impact-Site-Verification: ba879fba-9c48-4cb2-bc5a-3910d59d97c4</div>

        {/* HERO */}
        <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
          <OptimizedImage
            src={heroImage}
            alt="Futures trading charts and prop firm comparison"
            priority
            width={1920}
            height={1080}
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-hero bg-black/75" />
          <div className="relative z-10 text-center max-w-5xl mx-auto px-4 py-16">
            <h1 className="text-5xl md:text-7xl font-bold text-foreground mb-6">
              Find The Best <span className="text-trading-primary">Futures Prop Firm</span>
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Compare prop firms, discover discounts, calculate drawdowns, and learn how to pass evaluations faster.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
              <Button
                variant="trading"
                size="lg"
                className="text-lg px-8 py-6"
                onClick={() => NavigationHelper.navigateTo("/prop-firms")}
              >
                Compare Prop Firms <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                variant="trading-outline"
                size="lg"
                className="text-lg px-8 py-6"
                onClick={() => NavigationHelper.navigateTo("/apex-trader-funding-review")}
              >
                Read Apex Review
              </Button>
            </div>
            <div className="flex flex-wrap gap-3 justify-center">
              {trustIndicators.map((t) => (
                <Badge key={t.label} variant="secondary" className="px-4 py-2 text-sm">
                  <t.icon className="h-4 w-4 mr-2 text-trading-primary" />
                  {t.label}
                </Badge>
              ))}
            </div>
          </div>
        </section>

        {/* FEATURED PROP FIRM REVIEWS */}
        <section className="py-20 px-4">
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-foreground mb-4">Featured Futures Prop Firm Reviews</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Rule-focused comparisons with evidence limits stated clearly.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredFirms.map((firm) => {
                const href = reviewMap[firm.id] || `/prop-firms`;
                return (
                  <Card
                    key={firm.id}
                    className="bg-gradient-card border-border shadow-card hover:shadow-glow transition-all duration-300 flex flex-col"
                  >
                    <CardHeader>
                      <div className="flex items-center justify-between mb-2">
                        <CardTitle className="text-xl">{firm.name}</CardTitle>
                        <Badge variant="outline" className="text-xs">Research profile</Badge>
                      </div>
                      <CardDescription>
                        {firm.profitSplit} profit split · {firm.accountSizes[0]}-{firm.accountSizes[firm.accountSizes.length - 1]}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col">
                      <ul className="space-y-2 mb-6 flex-1">
                        {firm.pros.slice(0, 3).map((pro) => (
                          <li key={pro} className="flex items-start text-sm text-muted-foreground">
                            <CheckCircle2 className="h-4 w-4 mr-2 mt-0.5 text-trading-primary flex-shrink-0" />
                            {pro}
                          </li>
                        ))}
                      </ul>
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => NavigationHelper.navigateTo(href)}
                      >
                        {firm.id === "apex" ? "Read Full Review" : "View Details"}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* COMPARISON TABLE */}
        <section className="py-20 px-4 bg-muted/10">
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-foreground mb-4">Futures Prop Firm Comparison</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Side-by-side specs for the most popular futures prop firms.
              </p>
            </div>

            <div className="overflow-x-auto rounded-lg border border-border bg-card">
              <table className="w-full text-sm">
                <thead className="bg-muted/30 text-foreground">
                  <tr>
                    <th className="text-left p-4 font-semibold">Firm</th>
                    <th className="text-left p-4 font-semibold">Account Sizes</th>
                    <th className="text-left p-4 font-semibold">Profit Split</th>
                    <th className="text-left p-4 font-semibold">Activation / Eval Fee</th>
                    <th className="text-left p-4 font-semibold">Drawdown</th>
                    <th className="text-left p-4 font-semibold">Payouts</th>
                    <th className="text-left p-4 font-semibold">Review</th>
                  </tr>
                </thead>
                <tbody>
                  {featuredFirms.map((firm) => (
                    <tr key={firm.id} className="border-t border-border hover:bg-muted/20">
                      <td className="p-4 font-medium text-foreground">{firm.name}</td>
                      <td className="p-4 text-muted-foreground">{firm.accountSizes.join(", ")}</td>
                      <td className="p-4 text-muted-foreground">{firm.profitSplit}</td>
                      <td className="p-4 text-muted-foreground">{firm.evaluationFee}</td>
                      <td className="p-4 text-muted-foreground">{firm.maxDrawdown}</td>
                      <td className="p-4 text-muted-foreground">{firm.payoutFrequency}</td>
                      <td className="p-4">
                        <Button
                          variant="link"
                          className="p-0 h-auto text-trading-primary"
                          onClick={() => NavigationHelper.navigateTo(reviewMap[firm.id] || "/prop-firms")}
                        >
                          {firm.id === "apex" ? "Read review" : "Compare"} <ArrowRight className="ml-1 h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="text-center mt-8">
              <Button
                variant="trading"
                size="lg"
                onClick={() => NavigationHelper.navigateTo("/prop-firms")}
              >
                View Full Comparison <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </section>

        {/* CALCULATORS */}
        <section className="py-20 px-4">
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-foreground mb-4">Trader Calculators</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Free tools built for prop-firm traders — drawdown, consistency, tick value, and risk.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {calculators.map((c) => (
                <Card
                  key={c.title}
                  className="bg-gradient-card border-border shadow-card hover:shadow-glow transition-all duration-300 cursor-pointer"
                  onClick={() => NavigationHelper.navigateTo(c.href)}
                >
                  <CardHeader>
                    <c.icon className="h-10 w-10 text-trading-primary mb-3" />
                    <CardTitle className="text-lg">{c.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="mb-4">{c.description}</CardDescription>
                    <Button variant="outline" size="sm" className="w-full">
                      Open Calculator <ArrowRight className="ml-2 h-3 w-3" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* EDUCATION (moved below) */}
        <section className="py-20 px-4 bg-muted/10">
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-foreground mb-4">Latest Trader Education</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Deep-dives on futures markets, risk, and prop-firm strategy.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {educationCards.map((card) => (
                <Card key={card.href} className="bg-gradient-card border-border shadow-card hover:shadow-glow transition-all duration-300">
                  <CardHeader>
                    <CardTitle className="text-lg">{card.title}</CardTitle>
                    <CardDescription>{card.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="outline" className="w-full" onClick={() => NavigationHelper.navigateTo(card.href)}>
                      {card.cta}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center mt-10">
              <Button variant="trading-outline" onClick={() => NavigationHelper.navigateTo("/educational-resources")}>
                View All Resources
              </Button>
            </div>
          </div>
        </section>

        {/* NEWSLETTER */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="container mx-auto">
            <div className="max-w-2xl mx-auto">
              <NewsletterSignup
                variant="card"
                title="Get Prop-Firm Discounts & Trading Insights"
                description="Weekly newsletter: prop-firm discount alerts, payout proof, and futures market analysis."
              />
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default HomePage;
